import { PropsWithChildren, useMemo } from 'react'
import { createContext } from 'lib/createContext'
import { VndlyTimesheetFilters, vndlyTimesheetFiltersSchema } from '../schema'
import {
  useGenerateVndlyTimesheetMutation,
  useVndlyTimesheetIssuesQuery,
} from 'queries/vndly'
import { useVndlyTimesheetFilterQueryState } from '../hooks/useVndlyTimesheetFilterQueryState'
import { useAuthContext } from 'components/auth'
import { getCompaniesInCompanyGroup } from 'lib/companies'

type VndlyTimesheetProviderState = {
  state: {
    filters: VndlyTimesheetFilters
    companies: { value: number; label: string }[]
    downloadState: {
      canDownload: boolean
      isPending: boolean
      isError: boolean
    }
    issuesState: {
      requiresWorkWhileAdminIntervention: boolean
      workOrderAssociationIssues: NonNullable<
        ReturnType<typeof useVndlyTimesheetIssuesQuery>['data']
      >['workOrderAssociationIssues']
      queryState: {
        isEnabled: boolean
        isLoading: boolean
        isError: boolean
        isFetched: boolean
      }
    }
  }
  actions: {
    setFilters: (filters: VndlyTimesheetFilters) => void
    downloadTimesheet: ReturnType<
      typeof useGenerateVndlyTimesheetMutation
    >['mutate']
  }
}

export const [VndlyTimesheetContext, useVndlyTimesheetContext] =
  createContext<VndlyTimesheetProviderState>({
    name: 'VndlyTimesheetContext',
  })

export function VndlyTimesheetProvider({ children }: PropsWithChildren) {
  const { company } = useAuthContext()

  const companiesOptions = useMemo(() => {
    if (!company) {
      return []
    }

    const companies = getCompaniesInCompanyGroup(company)

    return companies
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({
        value: c.id,
        label: c.name,
      }))
  }, [company])

  const [filters, setFilters] = useVndlyTimesheetFilterQueryState(
    companiesOptions.map((c) => c.value)
  )

  const isIssuesQueryEnabled =
    vndlyTimesheetFiltersSchema.safeParse(filters).success

  const {
    mutate: downloadTimesheet,
    isPending: isDownloadPending,
    isError: isDownloadError,
  } = useGenerateVndlyTimesheetMutation({
    companyIds: filters.companyIds,
    startDate: filters.dateFrom,
    endDate: filters.dateTo,
  })

  const {
    data: issuesData,
    isLoading: isIssuesLoading,
    isError: isIssuesError,
    isFetched: isIssuesFetched,
  } = useVndlyTimesheetIssuesQuery(
    {
      companyIds: filters.companyIds,
      startDate: filters.dateFrom,
      endDate: filters.dateTo,
    },
    isIssuesQueryEnabled
  )

  const canDownload = useMemo(() => {
    return (
      isIssuesQueryEnabled && !issuesData?.requiresWorkWhileAdminIntervention
    )
  }, [isIssuesQueryEnabled, issuesData])

  const value = useMemo(() => {
    return {
      state: {
        filters,
        companies: companiesOptions,
        downloadState: {
          canDownload,
          isPending: isDownloadPending,
          isError: isDownloadError,
        },
        issuesState: {
          requiresWorkWhileAdminIntervention:
            issuesData?.requiresWorkWhileAdminIntervention ?? false,
          workOrderAssociationIssues:
            issuesData?.workOrderAssociationIssues ?? [],
          queryState: {
            isEnabled: isIssuesQueryEnabled,
            isLoading: isIssuesLoading,
            isError: isIssuesError,
            isFetched: isIssuesFetched,
          },
        },
      },
      actions: {
        setFilters,
        downloadTimesheet,
      },
    } satisfies VndlyTimesheetProviderState
  }, [
    filters,
    companiesOptions,
    setFilters,
    downloadTimesheet,
    isDownloadPending,
    isDownloadError,
    canDownload,
    issuesData,
    isIssuesQueryEnabled,
    isIssuesLoading,
    isIssuesError,
    isIssuesFetched,
  ])

  return (
    <VndlyTimesheetContext.Provider value={value}>
      {children}
    </VndlyTimesheetContext.Provider>
  )
}
